import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { EditModeProvider } from '@/contexts/EditModeContext';
import { Header } from '@/components/report/Header';
import { HeroSection } from '@/components/report/HeroSection';
import { AssessmentSummary } from '@/components/report/AssessmentSummary';
import { KeyInfoSection } from '@/components/report/KeyInfoSection';
import { RepairsSection } from '@/components/report/RepairsSection';
import { RTOVerificationSection } from '@/components/report/RTOVerificationSection';
import { OBDReportMobile } from '@/components/report/OBDReportMobile';
import { InspectionDetailsSection } from '@/components/report/InspectionDetailsSection';
import { VehicleDetailsSection } from '@/components/report/VehicleDetailsSection';
import { Footer } from '@/components/report/Footer';
import { ServiceHistorySection } from '@/components/report/ServiceHistorySection';
import { Skeleton } from '@/components/ui/skeleton';
import { getDownloadToken } from '@/lib/downloadToken';
import { Button } from '@/components/ui/button';
import DownloadReportDialog from '@/components/DownloadReportDialog';
import { AlertTriangle, ArrowLeft, Lock, Loader2, Download, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import '@/styles/inspection-report.css';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Loading skeleton component
function ReportSkeleton() {
  return (
    <div className="inspection-report min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// Authentication required component
function AuthRequired() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="h-8 w-8 text-amber-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Required</h2>
        <p className="text-gray-600 mb-6">
          This is an internal preview. Please log in to the CRM to access the report preview.
        </p>
        <Button onClick={() => navigate('/login')} className="w-full">
          Go to Login
        </Button>
      </div>
    </div>
  );
}

// Error component
function ReportError({ error, onRetry, onBack }) {
  return (
    <div className="inspection-report min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-red-600 mb-2">Failed to load report</h2>
        <p className="text-gray-500 mb-4">{error}</p>
        <div className="flex gap-2 justify-center">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
          <Button onClick={onRetry}>
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Transform Q&A categories from live progress to report format
 * Maps the backend category structure to what InspectionDetailsSection expects
 */
function transformCategoriesToReportFormat(categories, categoryRatings = {}, aiInsights = {}) {
  if (!categories || !Array.isArray(categories) || categories.length === 0) {
    return [];
  }
  
  // AI rating lookup — same logic as Q&A tab in LiveProgressModal
  const getAICategoryRating = (catName, catKey) => {
    const aiCategoryRatings = aiInsights.category_ratings || {};
    if (aiCategoryRatings[catName]) { const r = aiCategoryRatings[catName]; return typeof r === 'object' ? r.rating : r; }
    if (aiCategoryRatings[catKey]) { const r = aiCategoryRatings[catKey]; return typeof r === 'object' ? r.rating : r; }
    const normalizeKey = (str) => (str?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_') || '').replace(/^_+|_+$/g, '');
    const normalizedCatKey = normalizeKey(catName);
    for (const [key, value] of Object.entries(aiCategoryRatings)) {
      if (normalizeKey(key) === normalizedCatKey || key.toLowerCase() === catName?.toLowerCase()) return typeof value === 'object' ? value.rating : value;
    }
    const aiConditions = aiInsights.condition_ratings || {};
    const name = catName?.toLowerCase() || '';
    const condMap = (cond) => { const c = cond?.toUpperCase(); return c === 'POOR' ? 2 : (c === 'AVERAGE' || c === 'FAIR') ? 5 : c === 'GOOD' ? 7 : c === 'EXCELLENT' ? 9 : null; };
    if (name.includes('engine') || name.includes('health')) return condMap(aiConditions.engine);
    if (name.includes('exterior')) return condMap(aiConditions.exterior);
    if (name.includes('interior')) return condMap(aiConditions.interior);
    if (name.includes('transmission')) return condMap(aiConditions.transmission);
    return null;
  };
  
  return categories.map((cat, index) => {
    // Transform questions to details format
    const details = (cat.questions || [])
      .filter(q => q.is_answered) // Only show answered questions
      .map(q => {
        // Determine status based on answer
        let status = 'good';
        const answer = q.answer;
        const answerLower = typeof answer === 'string' ? answer.toLowerCase() : '';
        
        // Check for poor/average indicators
        if (answerLower.includes('poor') || answerLower.includes('bad') || answerLower.includes('damaged') || 
            answerLower.includes('broken') || answerLower.includes('not working') || answerLower.includes('faulty')) {
          status = 'poor';
        } else if (answerLower.includes('average') || answerLower.includes('fair') || answerLower.includes('needs attention') ||
                   answerLower.includes('worn') || answerLower.includes('minor')) {
          status = 'average';
        }
        
        // Determine type based on answer content
        let type = 'qa';
        let media = null;
        
        if (q.media_url) {
          if (q.question_type === 'video' || (q.media_url && q.media_url.includes('video'))) {
            type = 'video';
          } else {
            type = 'photo';
          }
          media = {
            thumbnail: q.media_url,
            url: q.media_url,
            duration: type === 'video' ? '0:30' : undefined
          };
        }
        
        // Helper to check if a string is a media URL/base64
        const isMediaString = (str) => {
          if (typeof str !== 'string') return false;
          return str.startsWith('data:image') || str.startsWith('data:video') || 
                 str.startsWith('http') || str.startsWith('media_ref:') ||
                 str.includes('firebase') || str.includes('base64');
        };

        // For photo/video: answer is the media, not useful as text
        const textAnswer = isMediaString(typeof answer === 'string' ? answer : '') ? null : (typeof answer === 'string' ? answer : (typeof answer === 'object' ? (answer?.selection || null) : null));

        return {
          item: q.question_text || 'Question',
          status: status,
          type: type,
          note: textAnswer || '',
          question: q.question_text,
          answer: textAnswer || '',
          media: media,
          captureInstruction: type !== 'qa' ? q.question_text : undefined,
          sub_answer_1: q.sub_answer_1 ? (typeof q.sub_answer_1 === 'object' ? (q.sub_answer_1.selection || '') : q.sub_answer_1) : undefined
        };
      });
    
    // Calculate auto-rating from answered questions
    const goodCount = details.filter(d => d.status === 'good').length;
    const totalAnswered = details.length;
    const autoRating = totalAnswered > 0 ? Math.round((goodCount / totalAnswered) * 10) : 0;
    
    // Rating fallback chain (matches Q&A tab logic exactly):
    // 1. Saved category_ratings  2. AI category/condition ratings  3. Auto-calculated
    const categoryKey = (cat.category_name || '').toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    const rawSavedRating = categoryRatings[categoryKey] || categoryRatings[cat.category_id];
    const savedRating = typeof rawSavedRating === 'object' && rawSavedRating !== null ? (rawSavedRating.rating ?? undefined) : rawSavedRating;
    const aiRating = getAICategoryRating(cat.category_name, categoryKey);
    const rating = savedRating ?? aiRating ?? autoRating;
    
    return {
      id: cat.category_id || `cat-${index}`,
      name: cat.category_name || 'General',
      icon: 'Settings', // Default icon
      rating: rating,
      checkpoints: cat.total_questions || 0,
      details: details
    };
  });
}

/**
 * Transform OBD data to report format
 * Maps raw OBD data to what OBDReportMobile expects.
 *
 * v2 (Feb 2026 — fix for prod report mismatch on car UP16CC0356):
 *   `inspection.obd_ai_analysis.codes_for_report` is the canonical single
 *   source of truth — it's what the OBD-2 tab and the AI analyser produce
 *   and persist. Previously this function read only from
 *   `live_progress_tracker.obd_scan.data.dtc_codes`, which is the RAW pre-
 *   analysis snapshot; for inspections where a PDF upload re-ran the AI
 *   analyser, the two diverged and the customer-facing Preview report
 *   showed a stale set of codes (different count, different ECU
 *   breakdown, "Stale (Memory)" tag).
 *
 *   Now: when `obd_ai_analysis.codes_for_report` exists, we trust it and
 *   build the systems grouping from its enriched fields directly. Legacy
 *   path retained as fallback for inspections that have only a raw scan
 *   without auto-analysis.
 */
function transformObdToReportFormat(obdScan, aiAnalysis = null) {
  // Fast path: AI-classified canonical codes from the auto-analyser.
  // Same data that powers the OBD-2 tab in the CRM.
  const aiCodes = aiAnalysis?.codes_for_report || [];
  if (aiCodes.length > 0) {
    return _buildObdReportFromAiCodes(aiCodes, obdScan, aiAnalysis);
  }
  
  if (!obdScan || !obdScan.completed) {
    return {
      connected: false,
      dtcCodes: [],
      totalErrors: 0,
      systems: []
    };
  }
  
  const obdData = obdScan.data || {};
  let rawCodes = obdData.raw_codes || obdData.dtc_codes || obdData.codes || [];
  
  // Filter codes based on report selection if available
  const dtcAnalysis = obdData.dtc_analysis;
  const reportSelection = obdScan.report_selection;
  
  if (reportSelection?.selected_codes && reportSelection.selected_codes.length > 0) {
    // Only include codes that were selected for the report
    const selectedSet = new Set(reportSelection.selected_codes);
    rawCodes = rawCodes.filter(codeEntry => {
      const code = typeof codeEntry === 'string' ? codeEntry : (codeEntry.code || '');
      return selectedSet.has(code);
    });
  }
  
  // If no errors, return clean state
  if (!rawCodes || rawCodes.length === 0) {
    return {
      connected: true,
      dtcCodes: [],
      totalErrors: 0,
      liveData: obdData.live_data || [],
      deviceName: obdData.device_name || '',
      protocol: obdData.protocol || '',
      scannedAt: obdScan.scanned_at || '',
      vin: obdData.vin || '',
      systems: [] // Empty systems = "No Diagnostic Errors" message
    };
  }
  
  // Create analysis lookup map
  const analysisMap = {};
  if (dtcAnalysis?.analyzed_codes) {
    dtcAnalysis.analyzed_codes.forEach(c => {
      analysisMap[c.code] = c;
    });
  }
  
  // Group codes by component (from AI analysis) or system type
  const systemMap = {};
  
  // Component name mapping (matching Live Progress Modal exactly)
  const componentFullNames = {
    'ECM': 'Engine Control Module',
    'TCM': 'Transmission Control Module',
    'ABS': 'Anti-lock Braking System',
    'BCM': 'Body Control Module',
    'HVAC': 'Climate Control System',
    'SRS': 'Airbag/Safety System',
    'PCM': 'Powertrain Control Module',
    'EPS': 'Electric Power Steering',
    'FUEL': 'Fuel System',
    'EMISSION': 'Emissions System',
    'ELECTRICAL': 'Electrical System',
    'NETWORK': 'CAN Bus/Network',
    'UNKNOWN': 'Uncategorized'
  };
  
  // Fallback category names for DTC prefix
  const categoryNames = {
    'P': 'Powertrain',
    'U': 'Network/Communication',
    'B': 'Body',
    'C': 'Chassis'
  };
  
  rawCodes.forEach(codeEntry => {
    const code = typeof codeEntry === 'string' ? codeEntry : (codeEntry.code || codeEntry.dtc_code || '');
    const description = typeof codeEntry === 'object' ? (codeEntry.description || codeEntry.meaning || `Diagnostic code ${code}`) : `Diagnostic code ${code}`;
    const analysis = analysisMap[code];
    
    // Use component from AI analysis (ECM, TCM, EMISSION, etc.) - matches Live Progress Modal
    // This groups by actual vehicle system/module, not just DTC category
    let systemKey = analysis?.component || 'UNKNOWN';
    let systemName = componentFullNames[systemKey] || systemKey;
    
    // Fallback to DTC prefix category if no AI component available
    if (systemKey === 'UNKNOWN' && code) {
      const prefix = code.charAt(0).toUpperCase();
      if (categoryNames[prefix]) {
        systemKey = prefix;
        systemName = categoryNames[prefix];
      }
    }
    
    // Determine severity from AI analysis
    let severity = 'warning';
    if (analysis?.severity === 'CRITICAL') severity = 'critical';
    else if (analysis?.severity === 'IMPORTANT') severity = 'warning';
    else if (analysis?.severity === 'NON_IMPORTANT') severity = 'info';
    
    const fault = {
      code: code,
      description: description,
      severity: severity,
      aiSeverity: analysis?.severity || null,
      aiReason: analysis?.reason || null,
      aiComponent: analysis?.component || null,
      status: codeEntry.status || 'Active',
      // v1.60.0: surface mechanic-app emitted status category so the report
      // can render Active / Pending / History / Stored badges. Older scans
      // (pre-v1.60.0) won't have this field — DtcStatusBadge falls back to
      // lowercased statusText, then defaults to 'stored'.
      statusCategory: codeEntry.statusCategory || codeEntry.status_category || null,
      statusText: codeEntry.statusText || codeEntry.status_text || codeEntry.status || null,
      // v1.61.0: surface backend demotion (ECU memory dump or contradictory pair)
      demotedReason: codeEntry.demotedReason || codeEntry.demoted_reason || (codeEntry.confidenceTier === 'demoted' ? 'ecu_memory_dump' : null),
      confidenceTier: codeEntry.confidenceTier || codeEntry.confidence_tier || null,
      ecuSource: codeEntry.ecuSource || '',
      // Check both analysis object AND raw code entry for these fields
      // Raw code may have them from the mechanic app's OBD scanner
      possibleCauses: analysis?.possible_causes || codeEntry.possible_causes || codeEntry.possibleCauses || [],
      symptoms: analysis?.symptoms || codeEntry.symptoms || [],
      solutions: analysis?.recommended_solutions || analysis?.solutions || codeEntry.recommended_solutions || codeEntry.solutions || []
    };
    
    if (!systemMap[systemKey]) {
      systemMap[systemKey] = {
        name: systemName,
        shortName: systemKey,
        code: systemKey,
        icon: systemKey === 'ECM' ? 'Cog' : 
              systemKey === 'TCM' ? 'Settings' : 
              systemKey === 'BCM' ? 'Shield' :
              systemKey === 'EMISSION' ? 'Cog' :
              systemKey === 'FUEL' ? 'Cog' :
              'Settings',
        faults: [],
        criticalCount: 0,
        importantCount: 0,
        nonImportantCount: 0
      };
    }
    
    systemMap[systemKey].faults.push(fault);
    
    // Count by severity
    if (analysis?.severity === 'CRITICAL') systemMap[systemKey].criticalCount++;
    else if (analysis?.severity === 'IMPORTANT') systemMap[systemKey].importantCount++;
    else systemMap[systemKey].nonImportantCount++;
  });
  
  // Build systems array sorted by critical count
  const systems = Object.values(systemMap)
    .filter(sys => sys.faults.length > 0)
    .sort((a, b) => (b.criticalCount * 100 + b.importantCount * 10) - (a.criticalCount * 100 + a.importantCount * 10))
    .map(sys => ({
      ...sys,
      errorCount: sys.faults.length
    }));
  
  // Build summary if analysis available
  const summary = dtcAnalysis?.summary || null;
  
  return {
    connected: true,
    dtcCodes: rawCodes,
    totalErrors: rawCodes.length,
    liveData: obdData.live_data || [],
    deviceName: obdData.device_name || '',
    protocol: obdData.protocol || '',
    scannedAt: obdScan.scanned_at || '',
    vin: obdData.vin || '',
    systems: systems,
    summary: summary,
    hasAiAnalysis: !!dtcAnalysis
  };
}

/**
 * Build the OBD report systems grouping directly from the AI-classified
 * canonical codes (`inspection.obd_ai_analysis.codes_for_report`). This is
 * the SAME data the CRM OBD-2 tab renders, so customer report and admin tab
 * stay perfectly in sync.
 */
function _buildObdReportFromAiCodes(aiCodes, obdScan, aiAnalysis) {
  const componentFullNames = {
    'ECM': 'Engine Control Module',
    'TCM': 'Transmission Control Module',
    'ABS': 'Anti-lock Braking System',
    'BCM': 'Body Control Module',
    'HVAC': 'Climate Control System',
    'SRS': 'Airbag/Safety System',
    'PCM': 'Powertrain Control Module',
    'EPS': 'Electric Power Steering',
    'FUEL': 'Fuel System',
    'EMISSION': 'Emissions System',
    'ELECTRICAL': 'Electrical System',
    'NETWORK': 'CAN Bus/Network',
    'CAN': 'CAN Bus/Network',
    'UNKNOWN': 'Uncategorized'
  };
  const systemMap = {};
  
  aiCodes.forEach(c => {
    const ecu = (c.ecuSource || c.ecu || c.component || 'UNKNOWN').toUpperCase();
    const systemKey = componentFullNames[ecu] ? ecu : 'UNKNOWN';
    const systemName = componentFullNames[systemKey] || ecu;
    
    // Map ai_severity → UI severity
    const aiSev = (c.ai_severity || c.severity || '').toUpperCase();
    let severity = 'warning';
    if (aiSev === 'CRITICAL') severity = 'critical';
    else if (aiSev === 'IMPORTANT') severity = 'warning';
    else if (aiSev === 'NON_IMPORTANT' || aiSev === 'MINOR') severity = 'info';
    
    const fault = {
      code: c.code,
      description: c.description || c.ai_explanation || `Diagnostic code ${c.code}`,
      severity,
      aiSeverity: aiSev || null,
      aiReason: c.ai_explanation || c.ai_reason || null,
      aiComponent: ecu,
      status: c.status || 'Active',
      statusCategory: c.statusCategory || c.status_category || null,
      statusText: c.statusText || c.status_text || c.status || null,
      demotedReason: c.demotedReason || (c.confidenceTier === 'demoted' ? 'ecu_memory_dump' : null),
      confidenceTier: c.confidenceTier || c.confidence_tier || null,
      ecuSource: ecu,
      possibleCauses: c.possible_causes || c.possibleCauses || [],
      symptoms: c.symptoms || [],
      solutions: c.recommended_actions || c.recommended_solutions || c.solutions || [],
    };
    
    if (!systemMap[systemKey]) {
      systemMap[systemKey] = {
        name: systemName,
        shortName: systemKey,
        code: systemKey,
        icon: systemKey === 'ECM' ? 'Cog'
            : systemKey === 'TCM' ? 'Settings'
            : systemKey === 'BCM' ? 'Shield'
            : systemKey === 'EMISSION' ? 'Cog'
            : systemKey === 'FUEL' ? 'Cog'
            : 'Settings',
        faults: [],
        criticalCount: 0,
        importantCount: 0,
        nonImportantCount: 0,
      };
    }
    systemMap[systemKey].faults.push(fault);
    if (severity === 'critical') systemMap[systemKey].criticalCount++;
    else if (severity === 'warning') systemMap[systemKey].importantCount++;
    else systemMap[systemKey].nonImportantCount++;
  });
  
  const systems = Object.values(systemMap)
    .filter(s => s.faults.length > 0)
    .sort((a, b) => (b.criticalCount * 100 + b.importantCount * 10) - (a.criticalCount * 100 + a.importantCount * 10))
    .map(s => ({ ...s, errorCount: s.faults.length }));
  
  return {
    connected: true,
    dtcCodes: aiCodes.map(c => ({ code: c.code, description: c.description, status: c.status })),
    totalErrors: aiCodes.length,
    liveData: obdScan?.data?.live_data || [],
    deviceName: obdScan?.data?.device_name || '',
    protocol: obdScan?.data?.protocol || '',
    scannedAt: obdScan?.scanned_at || aiAnalysis?.analyzed_at || '',
    vin: obdScan?.data?.vin || '',
    systems,
    summary: aiAnalysis?.summary || null,
    hasAiAnalysis: true,
  };
}

/**
 * Transform CRM inspection data to report format
 * Maps all fields from LiveProgressModal to InspectionReport structure
 */
function transformInspectionToReport(inspection, lead, customer, liveProgressData = {}, repairsData = {}) {
  // Extract AI insights if available
  const aiInsights = inspection.ai_insights || {};
  const conditionRatings = aiInsights.condition_ratings || {};
  const vaahanData = inspection.vaahan_data || {};
  
  // Get OBD data from live progress
  const obdScan = liveProgressData.obd_scan || {};
  
  // Get Q&A categories from live progress and transform to report format
  const rawCategories = liveProgressData.categories || [];

  // Extract accident/flood status from Q&A answers in General Information category
  // These questions have Yes/No MCQ answers that should override inspection-level fields
  const qaAccidentFlood = (() => {
    let isAccidental = null;
    let isFloodDamaged = null;
    for (const cat of rawCategories) {
      const catName = (cat.category_name || '').toLowerCase();
      if (!catName.includes('general')) continue;
      for (const q of (cat.questions || [])) {
        if (!q.is_answered) continue;
        const qText = (q.question_text || '').toLowerCase();
        const answer = q.answer;
        // Extract the selection value from MCQ answers (can be string or {selection: "Yes"})
        const selection = (typeof answer === 'string' ? answer : (answer?.selection || '')).toLowerCase().trim();
        if (qText.includes('accident')) {
          isAccidental = selection === 'yes';
        }
        if (qText.includes('flood')) {
          isFloodDamaged = selection === 'yes';
        }
      }
    }
    return { isAccidental, isFloodDamaged };
  })();
  let transformedCategories = [];
  try {
    transformedCategories = transformCategoriesToReportFormat(rawCategories, inspection.category_ratings || {}, aiInsights);
  } catch (e) {
    console.error('Error transforming categories for report:', e);
  }
  
  // Calculate conditions from category ratings (same logic as LiveProgressModal)
  // This ensures Preview Report shows same values as AI Analysis tab
  const calculateConditionFromRatings = (categoryRatings, groupKeywords) => {
    if (!categoryRatings || typeof categoryRatings !== 'object') return null;
    
    const ratings = [];
    Object.entries(categoryRatings).forEach(([key, value]) => {
      const keyLower = (key || '').toLowerCase();
      const rating = typeof value === 'object' && value !== null ? (value.rating ?? 0) : (value ?? 0);
      
      // Check if this category belongs to the group
      const matchesGroup = groupKeywords.some(keyword => keyLower.includes(keyword));
      if (matchesGroup && rating > 0) {
        ratings.push(rating);
      }
    });
    
    if (ratings.length === 0) return null;
    const avg = Math.round(ratings.reduce((a, b) => a + b, 0) / ratings.length);
    
    // Convert rating to condition: 8-10 = GOOD, 5-7 = AVERAGE, 1-4 = POOR
    if (avg >= 8) return 'GOOD';
    if (avg >= 5) return 'AVERAGE';
    if (avg >= 1) return 'POOR';
    return 'PENDING';
  };
  
  // Normalize condition - convert FAIR to AVERAGE for consistency
  const normalizeCondition = (cond) => {
    if (!cond) return 'PENDING';
    const upper = cond.toUpperCase();
    // FAIR -> AVERAGE for consistency
    if (upper === 'FAIR') return 'AVERAGE';
    return upper;
  };
  
  // Group keywords for each condition type
  const engineKeywords = ['engine', 'exhaust', 'oil', 'battery', 'electrical', 'obd', 'scanner', 'health', 'diagnosis'];
  const exteriorKeywords = ['exterior', 'bumper', 'fender', 'headlight', 'taillight', 'hood', 'trunk', 'windshield', 'glass', 'door panel', 'tyre', 'tire', 'wheel', 'physical'];
  const interiorKeywords = ['interior', 'infotainment', 'air condition', 'ac ', 'cooling', 'accessories'];
  const transmissionKeywords = ['transmission', 'gearbox', 'clutch', 'suspension', 'brake'];
  
  // Calculate conditions from category ratings
  const calculatedConditions = {
    engine: calculateConditionFromRatings(inspection.category_ratings, engineKeywords),
    exterior: calculateConditionFromRatings(inspection.category_ratings, exteriorKeywords),
    interior: calculateConditionFromRatings(inspection.category_ratings, interiorKeywords),
    transmission: calculateConditionFromRatings(inspection.category_ratings, transmissionKeywords)
  };
  
  // Transform OBD data to report format.
  // Pass `inspection.obd_ai_analysis` so the canonical AI-classified codes
  // (the same set rendered by the CRM OBD-2 tab) are used directly. Falls
  // back to legacy live_progress_tracker.obd_scan.data when un-analysed.
  const transformedObd = transformObdToReportFormat(obdScan, inspection.obd_ai_analysis);
  
  // Extract repairs data.
  // PERMANENT FIX (Feb 2026): the backend's `inspection.repairs_final` is the
  // single source of truth — same data shown in the Repairs tab, the
  // published-report snapshot, and the PDF. If present, trust it directly
  // (no client-side filtering, no drift). Fallback to the legacy filter only
  // when the inspection hasn't been migrated yet (pre-backfill data).
  const finalRepairs = inspection.repairs_final;
  let minorRepairs;
  let majorRepairs;
  
  if (finalRepairs && (Array.isArray(finalRepairs.minor_repairs) || Array.isArray(finalRepairs.major_repairs))) {
    // Fast path: read denormalized field
    minorRepairs = finalRepairs.minor_repairs || [];
    majorRepairs = finalRepairs.major_repairs || [];
  } else {
    // Legacy path: filter client-side using saved selection (kept for
    // pre-backfill inspections; removed once backfill completes 100%).
    const minorRepairsRaw = repairsData.minor_repairs || [];
    const majorRepairsRaw = repairsData.major_repairs || [];
    const savedSelection = inspection.repair_report_selection;
    minorRepairs = minorRepairsRaw;
    majorRepairs = majorRepairsRaw;
    if (savedSelection && Array.isArray(savedSelection.selected_repairs)) {
      const selectedSet = new Set(savedSelection.selected_repairs);
      minorRepairs = minorRepairsRaw.filter(r => r.source === 'manual' || selectedSet.has(r.part_id || r.id));
      majorRepairs = majorRepairsRaw.filter(r => r.source === 'manual' || selectedSet.has(r.part_id || r.id));
    } else {
      // NEW DEFAULT: un-curated inspection → zero rule-calc repairs
      // (matches backend `data_utils.get_inspection_repairs_data` semantics).
      minorRepairs = minorRepairsRaw.filter(r => r.source === 'manual');
      majorRepairs = majorRepairsRaw.filter(r => r.source === 'manual');
    }
  }

  const totalMinor = minorRepairs.reduce((sum, r) => sum + (r.cost || 0), 0);
  const totalMajor = majorRepairs.reduce((sum, r) => sum + (r.cost || 0), 0);
  
  // Handle assessment_summary - it can be a string or an object
  const getAssessmentText = () => {
    const summary = inspection.assessment_summary || aiInsights.assessment_summary;
    if (!summary) return "This vehicle has been professionally inspected. Detailed assessment available below.";
    if (typeof summary === 'string') return summary;
    // If it's an object, combine the values into a readable paragraph
    if (typeof summary === 'object') {
      const parts = [];
      if (summary.overall) parts.push(summary.overall);
      if (summary.engine_and_mechanical) parts.push(`Engine & Mechanical: ${summary.engine_and_mechanical}`);
      if (summary.exterior_body) parts.push(`Exterior: ${summary.exterior_body}`);
      if (summary.interior_comfort) parts.push(`Interior: ${summary.interior_comfort}`);
      if (summary.safety_systems) parts.push(`Safety: ${summary.safety_systems}`);
      if (summary.documentation) parts.push(`Documentation: ${summary.documentation}`);
      return parts.join(' ') || "This vehicle has been professionally inspected. Detailed assessment available below.";
    }
    return "This vehicle has been professionally inspected. Detailed assessment available below.";
  };
  
  // Build comprehensive report data structure
  const reportData = {
    header: {
      reportFor: inspection.package_name || "Vehicle Inspection Report",
      customerName: inspection.customer_name || customer?.name || lead?.customer_name || "Customer",
      customerPhone: inspection.customer_mobile || customer?.phone || lead?.phone_number || "",
      vehicleNumber: inspection.car_number || lead?.vehicle_number || "",
      inspectionRequestedDate: lead?.created_at 
        ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) 
        : inspection.created_at 
          ? new Date(inspection.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : "-",
      inspectedOn: inspection.inspection_date 
        ? new Date(inspection.inspection_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) 
        : inspection.inspection_completed_at
          ? new Date(inspection.inspection_completed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : inspection.updated_at
            ? new Date(inspection.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : "-",
      inspectedBy: inspection.mechanic_name || "WiseDrive Mechanic",
      inspectionType: inspection.package_name || "Standard Inspection",
      location: inspection.city || lead?.city || "",
      marketValue: {
        min: inspection.market_value_min || aiInsights.market_value?.min || 0,
        max: inspection.market_value_max || aiInsights.market_value?.max || 0,
        currency: "₹",
        confidence: aiInsights.market_value?.confidence || "medium"
      },
      recommendedToBuy: inspection.recommended_to_buy ?? aiInsights.recommended_to_buy ?? false,
      overallRating: inspection.overall_rating || aiInsights.overall_rating || 0,
      checkpointsInspected: inspection.checkpoints_inspected || 0,
      isPublished: inspection.report_published || false,
      lastSaved: inspection.updated_at ? new Date(inspection.updated_at).toLocaleString('en-IN') : "-",
      aiGenerated: aiInsights.ai_generated || !!inspection.ai_report_generated_at,
      aiGeneratedAt: aiInsights.generated_at || inspection.ai_report_generated_at || null
    },

    vehicleInfo: {
      make: inspection.vehicle_make || vaahanData.manufacturer || lead?.vehicle_make || "",
      model: inspection.vehicle_model || vaahanData.model || lead?.vehicle_model || "",
      year: inspection.vehicle_year || vaahanData.manufacturing_date?.split('/')[1] || lead?.vehicle_year || 0,
      mfgDate: vaahanData.manufacturing_date || inspection.mfg_date || "",
      fuel: inspection.fuel_type || vaahanData.fuel_type || lead?.fuel_type || "",
      transmission: inspection.transmission || "",
      owners: inspection.owners || parseInt(vaahanData.owner_count) || 0,
      regNo: inspection.car_number || vaahanData.registration_number || lead?.vehicle_number || "",
      colour: inspection.vehicle_colour || vaahanData.color || "",
      regDate: vaahanData.registration_date || inspection.reg_date || "",
      engineCC: inspection.engine_cc || parseInt(vaahanData.cubic_capacity) || 0,
      engineNo: vaahanData.engine_number || inspection.engine_number || inspection.engine_no || "",
      chassisNo: vaahanData.chassis_number || inspection.chassis_number || inspection.chassis_no || ""
    },

    assessmentSummary: {
      paragraph: getAssessmentText(),
      keyHighlights: inspection.key_highlights || aiInsights.key_highlights || [],
      riskFactors: aiInsights.risk_factors || [],
      recommendations: aiInsights.recommendations || []
    },

    keyInfo: {
      kmsDriven: inspection.kms_driven || lead?.kms_driven || 0,
      // IMPORTANT: Condition logic - prioritize DB-saved values over calculated
      // 1. If DB has a non-PENDING saved value → use it (user explicitly saved it)
      // 2. If DB is PENDING and we have calculated value → use calculated
      // 3. Else use PENDING
      engineCondition: normalizeCondition(
        (inspection.engine_condition && inspection.engine_condition !== 'PENDING') 
          ? inspection.engine_condition 
          : (calculatedConditions.engine || "PENDING")
      ),
      interiorCondition: normalizeCondition(
        (inspection.interior_condition && inspection.interior_condition !== 'PENDING')
          ? inspection.interior_condition
          : (calculatedConditions.interior || "PENDING")
      ),
      transmission: normalizeCondition(
        (inspection.transmission_condition && inspection.transmission_condition !== 'PENDING')
          ? inspection.transmission_condition
          : (calculatedConditions.transmission || "PENDING")
      ),
      transmissionCondition: normalizeCondition(
        (inspection.transmission_condition && inspection.transmission_condition !== 'PENDING')
          ? inspection.transmission_condition
          : (calculatedConditions.transmission || "PENDING")
      ),
      exteriorCondition: normalizeCondition(
        (inspection.exterior_condition && inspection.exterior_condition !== 'PENDING')
          ? inspection.exterior_condition
          : (calculatedConditions.exterior || "PENDING")
      ),
      // Accident & Flood: Q&A answer takes priority, then fall back to inspection-level fields
      accident: qaAccidentFlood.isAccidental !== null ? qaAccidentFlood.isAccidental : (inspection.accident_history || false),
      floodDamage: qaAccidentFlood.isFloodDamaged !== null ? qaAccidentFlood.isFloodDamaged : (inspection.flood_damage || false),
      dentsScratches: inspection.dents_scratches || false,
      
      insurance: (() => {
        // Determine insurance status based on expiry date from Vaahan API
        let insuranceStatus = inspection.insurance_status;
        if (!insuranceStatus || insuranceStatus === 'Unknown') {
          const expiryDate = inspection.insurance_expiry || vaahanData.insurance_valid_upto;
          if (expiryDate) {
            const expiry = new Date(expiryDate);
            insuranceStatus = expiry > new Date() ? 'Active' : 'Expired';
          } else {
            insuranceStatus = 'Unknown';
          }
        }
        return {
          status: insuranceStatus,
          insurerName: inspection.insurer_name || vaahanData.insurance_company || "",
          policyNumber: inspection.policy_number || vaahanData.insurance_policy_number || "",
          expiryDate: inspection.insurance_expiry || vaahanData.insurance_valid_upto || "",
          policyType: inspection.policy_type || "",
          idvValue: inspection.idv_value || 0
        };
      })(),
      
      tyreDetails: {
        avgLife: inspection.tyre_avg_life || 0,
        tyres: inspection.tyre_details || []
      },
      
      // Use ONLY repairs from /calculated-repairs API endpoint
      // The API handles prioritization: manual repairs > calculated repairs > AI repairs
      // The API also filters out zero-cost repairs
      // DO NOT use inspection.calculated_repairs as it may contain stale/zero-cost items
      repairs: [], // Empty - let RepairsSection use minorRepairs + majorRepairs only
      calculatedRepairs: [],
      minorRepairs: minorRepairs,
      majorRepairs: majorRepairs,
      totalMinorRepairsCost: totalMinor,
      totalMajorRepairsCost: totalMajor,
      totalRepairCostMin: inspection.total_repair_cost_min || totalMinor + totalMajor,
      totalRepairCostMax: inspection.total_repair_cost_max || Math.round((totalMinor + totalMajor) * 1.2)
    },

    rtoVerification: {
      status: inspection.rto_verification_status || (vaahanData.status ? "VERIFIED" : "PENDING"),
      challans: inspection.challan_data?.challan_details || vaahanData.challans || inspection.challans || [],
      trafficChallans: inspection.challan_data?.total_pending_amount || 0,
      totalChallans: inspection.challan_data?.total_challans || 0,
      pendingCount: inspection.challan_data?.pending_count || 0,
      hypothecation: inspection.hypothecation || vaahanData.hypothecation || null,
      blacklist: inspection.blacklist_status || vaahanData.blacklist_status || false,
      registrationAuthority: vaahanData.registration_authority || "",
      registrationValidity: vaahanData.rc_expiry_date || "",
      fitnessValidity: vaahanData.fitness_upto || "",
      taxValidity: vaahanData.tax_valid_upto || "",
      insuranceValidity: vaahanData.insurance_valid_upto || "",
      puccValidity: vaahanData.pucc_valid_upto || "",
      noc: vaahanData.noc_status || "",
      financed: vaahanData.financed || false,
      financier: vaahanData.financer || "",
      puccNumber: vaahanData.pucc_number || ""
    },

    // OBD Report - transformed from live progress data with proper systems structure
    // Only include if OBD scanning is enabled for this inspection
    obdReport: inspection.obd_scanning_enabled !== false ? transformedObd : null,
    obdScanningEnabled: inspection.obd_scanning_enabled !== false,

    // Q&A Categories - transformed from live progress data with proper details structure
    inspectionCategories: transformedCategories.length > 0 ? transformedCategories : (inspection.inspection_categories || []),
    categoryRatings: inspection.category_ratings || aiInsights.category_ratings || {},

    footer: {
      companyName: "WiseDrive",
      supportPhone: "+91 88848 54885",
      supportEmail: "support@wisedrive.in",
      website: "www.wisedrive.in"
    },
    
    // Include raw data for advanced components
    aiInsights: aiInsights,
    vaahanData: vaahanData
  };

  return reportData;
}

/**
 * Preview Report Page - CRM Internal Only
 * This page requires authentication and is used for previewing reports before publishing
 */
function PreviewReportContent({ inspectionId }) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const handleDownloadPdf = async (brand = 'wisedrive') => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Phase 1: Generate PDF on server
      const prepareRes = await fetch(`${apiUrl}/api/inspections/${inspectionId}/report-pdf-prepare?brand=${brand}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!prepareRes.ok) {
        const err = await prepareRes.json().catch(() => ({}));
        throw new Error(err.detail || 'PDF generation failed');
      }
      const { cache_key, filename } = await prepareRes.json();
      
      // Phase 2: Fetch as blob (VAPT-08: scoped short-lived download token)
      const dlToken = await getDownloadToken('cached-pdf', cache_key);
      const downloadUrl = `${apiUrl}/api/report-pdf-download/${cache_key}?token=${encodeURIComponent(dlToken)}`;
      const pdfRes = await fetch(downloadUrl);
      if (!pdfRes.ok) throw new Error('Download failed');
      const blob = await pdfRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'report.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('PDF download error:', err);
      alert(err.message || 'Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fetchReport = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get auth token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Fetch inspection report, live-progress data, and calculated repairs in parallel
      const [reportResponse, liveProgressResponse, repairsResponse] = await Promise.all([
        axios.get(
          `${API_URL}/api/inspections/${inspectionId}/report`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${API_URL}/api/inspections/${inspectionId}/live-progress`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => ({ data: {} })), // Fallback if live-progress fails
        axios.get(
          `${API_URL}/api/inspections/${inspectionId}/calculated-repairs`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => ({ data: { minor_repairs: [], major_repairs: [], total_minor: 0, total_major: 0 } })) // Fallback
      ]);
      
      const { inspection, lead, customer } = reportResponse.data;
      const liveProgressData = liveProgressResponse.data;
      const repairsData = repairsResponse.data;
      
      // Transform to report format, including live progress data and repairs
      const reportData = transformInspectionToReport(inspection, lead, customer, liveProgressData, repairsData);
      setData(reportData);
    } catch (err) {
      console.error('Error fetching report:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Inspection not found');
      } else {
        setError(err.response?.data?.detail || 'Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inspectionId && user) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId, user]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Require authentication
  if (!user) {
    return <AuthRequired />;
  }

  if (loading) return <ReportSkeleton />;
  if (error) return <ReportError error={error} onRetry={fetchReport} onBack={() => navigate(-1)} />;
  if (!data) return null;

  return (
    <div className="inspection-report min-h-screen">
      {/* Preview Banner */}
      <div className="bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium sticky top-0 z-50 flex items-center justify-center gap-2">
        <span className="mr-1">INTERNAL PREVIEW</span>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="text-white hover:bg-amber-600"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDownloadDialogOpen(true)}
          className="text-white hover:bg-amber-600"
          data-testid="preview-download-pdf-btn"
        >
          <Download className="h-4 w-4 mr-1" />
          Download Report
        </Button>
      </div>

      <DownloadReportDialog
        open={downloadDialogOpen}
        onOpenChange={setDownloadDialogOpen}
        inspectionId={inspectionId}
        context="admin"
        defaultFormat="wisedrive"
        twoStep={true}
      />
      
      {/* Header */}
      <Header data={data.header} />
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto pb-8">
        {/* Hero Section */}
        <HeroSection 
          header={data.header} 
          vehicleInfo={data.vehicleInfo}
          keyInfo={data.keyInfo}
        />
        
        {/* AI Assessment Summary */}
        <AssessmentSummary data={data.assessmentSummary} />
        
        {/* Vehicle Details (Collapsible) */}
        <VehicleDetailsSection data={data.vehicleInfo} />
        
        {/* Key Information with Modals */}
        <KeyInfoSection data={data.keyInfo} inspectionCategories={data.inspectionCategories} />
        
        {/* RTO Verification - right below Key Info */}
        <RTOVerificationSection data={data.rtoVerification} />
        
        {/* Comprehensive Repairs Section */}
        <RepairsSection data={data.keyInfo} />
        
        {/* OBD-2 Report - Hidden when OBD scanning is disabled */}
        {data.obdScanningEnabled && <OBDReportMobile data={data.obdReport} />}
        
        {/* Inspection Details with Horizontal Scroll */}
        <InspectionDetailsSection data={data.inspectionCategories} />
        
        {/* Authorized Service History */}
        <ServiceHistorySection inspectionId={inspectionId} />
      </main>
      
      {/* Footer */}
      <Footer data={data.footer} />
    </div>
  );
}

export default function PreviewReportPage() {
  const { id } = useParams();

  if (!id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Inspection ID</h2>
          <p className="text-gray-500">Please provide an inspection ID to preview the report.</p>
        </div>
      </div>
    );
  }

  return (
    <EditModeProvider>
      <PreviewReportContent inspectionId={id} />
    </EditModeProvider>
  );
}
